import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Plus, Minus, X, ShoppingBag, ShieldAlert, Sparkles } from "lucide-react";
import { rupees } from "@/lib/api";
import { LOYALTY_SILVER_MIN, LOYALTY_GOLD_MIN, LOYALTY_TIER_META } from "@/lib/loyalty";

export default function Cart() {
  const { user } = useAuth();
  const { items, updateItem, subtotal, totalItems, loyalty, monthlySpend } = useCart() || { items: [], updateItem: () => {}, subtotal: 0, totalItems: 0, loyalty: { amount: 0, tier: "none", percent: 0 }, monthlySpend: 0 };
  const navigate = useNavigate();
  const deliveryFee = subtotal >= 500 || subtotal === 0 ? 0 : 40;
  const loyaltyDiscount = loyalty.amount;
  const total = Math.max(subtotal + deliveryFee - loyaltyDiscount, 0);
  const rxItems = items.filter((i) => i.medicine.prescription_required);
  const tierMeta = LOYALTY_TIER_META[loyalty.tier] || LOYALTY_TIER_META.none;
  const nextThreshold =
    loyalty.tier === "elite"        ? null :
    subtotal < LOYALTY_SILVER_MIN   ? { amount: LOYALTY_SILVER_MIN, percent: 10 } :
    subtotal < LOYALTY_GOLD_MIN     ? { amount: LOYALTY_GOLD_MIN,   percent: 15 } : null;

  return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="label-caps text-stone-500">Basket</div>
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1A362B] tracking-tight mt-2 mb-10">Your cart</h1>

        {!user ? (
          <div className="border border-dashed border-stone-300 rounded-md p-12 text-center">
            <ShoppingBag className="w-8 h-8 mx-auto text-stone-400"/>
            <p className="font-serif-display text-2xl text-[#1A362B] mt-4">Sign in to view your cart</p>
            <Button onClick={() => navigate("/login")} className="mt-6 rounded-full bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6]" data-testid="cart-signin-cta">Sign in</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-stone-300 rounded-md p-12 text-center">
            <ShoppingBag className="w-8 h-8 mx-auto text-stone-400"/>
            <p className="font-serif-display text-2xl text-[#1A362B] mt-4">Your cart is empty</p>
            <p className="text-stone-500 mt-2 text-sm">Add some medicines from the shop.</p>
            <Link to="/shop"><Button className="mt-6 rounded-full bg-[#D96C4A] hover:bg-[#E67D5C] text-white" data-testid="empty-cart-shop">Browse medicines</Button></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-4">
              {rxItems.length > 0 && (
                <div className="p-4 border-l-4 border-[#1A362B] bg-[#1A362B]/5 rounded-r-md flex items-start gap-3" data-testid="cart-rx-notice">
                  <ShieldAlert className="w-5 h-5 text-[#1A362B] mt-0.5 shrink-0"/>
                  <div className="text-sm text-[#1A362B]">
                    <div className="font-semibold">Prescription required</div>
                    <div className="text-stone-600 mt-1">
                      Your cart has {rxItems.length} prescription-only item{rxItems.length > 1 ? "s" : ""}. You&apos;ll need to upload a doctor&apos;s prescription at checkout — orders ship only after our pharmacist verifies it.
                    </div>
                  </div>
                </div>
              )}
              {items.map((it) => (
                <div key={it.medicine.id} className="flex items-center gap-4 p-4 border border-stone-200 rounded-md bg-white" data-testid={`cart-row-${it.medicine.id}`}>
                  <div className="w-20 h-20 rounded-sm bg-[#FFFFFF] overflow-hidden shrink-0">
                    <img src={it.medicine.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="label-caps text-[10px] text-stone-500">{it.medicine.brand}</div>
                      {it.medicine.prescription_required && (
                        <span className="label-caps text-[9px] px-2 py-0.5 bg-[#1A362B] text-[#EFE7D6] rounded-sm">Rx</span>
                      )}
                    </div>
                    <div className="font-serif-display text-lg text-[#1A362B] truncate">{it.medicine.name}</div>
                    <div className="font-mono-num text-sm text-stone-600 mt-1">{rupees(it.medicine.price)} each</div>
                  </div>
                  <div className="flex items-center border border-stone-300 rounded-full h-9 px-1">
                    <button onClick={() => updateItem(it.medicine.id, it.quantity - 1)} className="w-7 h-7 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid={`cart-dec-${it.medicine.id}`}><Minus className="w-3 h-3"/></button>
                    <span className="w-8 text-center font-mono-num text-sm">{it.quantity}</span>
                    <button onClick={() => updateItem(it.medicine.id, it.quantity + 1)} className="w-7 h-7 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid={`cart-inc-${it.medicine.id}`}><Plus className="w-3 h-3"/></button>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-num text-base text-[#1A362B]">{rupees(it.medicine.price * it.quantity)}</div>
                    <button onClick={() => updateItem(it.medicine.id, 0)} className="text-xs text-stone-400 hover:text-[#C44949] mt-1 inline-flex items-center gap-1" data-testid={`cart-remove-${it.medicine.id}`}><X className="w-3 h-3"/> Remove</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-1">
              <div className="p-6 border border-stone-200 rounded-md bg-white sticky top-24">
                <div className="label-caps text-stone-500 text-xs">Summary</div>
                <div className="mt-4 space-y-3 font-mono-num text-sm">
                  <div className="flex justify-between"><span className="text-stone-600">Items ({totalItems})</span><span>{rupees(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">Delivery</span><span>{deliveryFee === 0 ? "Free" : rupees(deliveryFee)}</span></div>
                  {loyaltyDiscount > 0 && (
                    <div className="flex justify-between" style={{ color: tierMeta.color }} data-testid="cart-loyalty-line">
                      <span className="inline-flex items-center gap-1">
                        {loyalty.tier === "elite" && <Sparkles className="w-3.5 h-3.5"/>}
                        {loyalty.tier === "elite" ? "Elite · 20% off" : loyalty.tier === "gold" ? "Gold · 15% off" : "Silver · 10% off"}
                      </span>
                      <span>− {rupees(loyaltyDiscount)}</span>
                    </div>
                  )}
                  {nextThreshold && subtotal > 0 && (
                    <p className="text-xs text-[#D96C4A]" data-testid="cart-loyalty-nudge">Add {rupees(nextThreshold.amount - subtotal)} more to unlock {nextThreshold.percent}% off</p>
                  )}
                  {loyalty.tier === "elite" && (
                    <p className="text-xs text-[#1A362B] font-serif-display italic" data-testid="cart-elite-badge">Elite shopper · 30-day spend ₹{monthlySpend.toFixed(0)}</p>
                  )}
                  {subtotal > 0 && subtotal < 500 && (
                    <p className="text-xs text-[#D96C4A]">Add {rupees(500 - subtotal)} more for free delivery</p>
                  )}
                </div>
                <Separator className="my-4"/>
                <div className="flex justify-between font-serif-display text-xl text-[#1A362B]">
                  <span>Total</span>
                  <span className="font-mono-num">{rupees(total)}</span>
                </div>
                <Button
                  onClick={() => navigate("/checkout")}
                  className="w-full mt-6 h-12 rounded-full bg-[#D96C4A] hover:bg-[#E67D5C] text-white active:scale-95 transition-transform"
                  data-testid="checkout-cta"
                >
                  Proceed to checkout
                </Button>
                <Link to="/shop" className="mt-4 block text-center text-xs label-caps text-stone-500 hover:text-[#1A362B]">Continue shopping</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
